<template>
  <!-- Modal sheet inside the chat panel. Direction is inherited from the panel's
       inner wrapper (chat locale), so logical spacing resolves RTL under Arabic. -->
  <div class="absolute inset-0 z-20 flex flex-col bg-white">
    <!-- Sheet header -->
    <div class="flex items-center gap-2 px-4 py-3 bg-[var(--color-primary)] text-white flex-shrink-0">
      <ClipboardList class="w-5 h-5 flex-shrink-0" />
      <p class="flex-1 min-w-0 font-semibold text-sm leading-tight truncate">{{ ct('chat_inquiry_cta') }}</p>
      <button type="button" @click="$emit('close')" :aria-label="ct('chat_close')" :title="ct('chat_close')"
        class="p-1.5 rounded-lg hover:bg-white/15 transition-colors">
        <X class="w-4 h-4" />
      </button>
    </div>

    <!-- Scrollable body -->
    <form @submit.prevent="submit" novalidate class="flex-1 overflow-y-auto px-4 py-4 bg-[#f7fafc] flex flex-col gap-3.5">
      <p class="text-xs text-[#4a5568] leading-snug">{{ ct('chat_inquiry_intro') }}</p>

      <!-- Attached DIY draft -->
      <div v-if="showDraftChip" class="flex items-start gap-2 bg-[var(--color-primary-light)] border border-[#cfe1f5] rounded-xl px-3 py-2">
        <Wrench class="w-4 h-4 text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-[#2d3748]">{{ ct('quote_draft_attached') }}</p>
          <p class="text-[11px] text-[#4a5568] mt-0.5 break-words">{{ draftSummary }}</p>
        </div>
        <button type="button" @click="includeDraft = false" :aria-label="ct('chat_remove')"
          class="p-1 rounded-lg text-[#4a5568] hover:bg-white/70 transition-colors flex-shrink-0">
          <X class="w-3.5 h-3.5" />
        </button>
      </div>

      <!-- Name -->
      <div>
        <label class="if-label">{{ ct('quote_name') }} <span class="text-red-500">*</span></label>
        <input v-model.trim="form.name" type="text" class="if-input" :class="{ 'if-input-error': errors.name }"
          @input="errors.name = ''" />
        <p v-if="errors.name" class="if-error">{{ ct('quote_required') }}</p>
      </div>

      <!-- Email -->
      <div>
        <label class="if-label">{{ ct('quote_email') }} <span class="text-red-500">*</span></label>
        <input v-model.trim="form.email" type="email" class="if-input" :class="{ 'if-input-error': errors.email }"
          @input="errors.email = ''" />
        <p v-if="errors.email" class="if-error">{{ errors.email === 'invalid' ? ct('quote_invalid_email') : ct('quote_required') }}</p>
      </div>

      <!-- Phone + Country -->
      <div class="grid grid-cols-2 gap-2.5">
        <div>
          <label class="if-label">{{ ct('quote_phone') }}</label>
          <input v-model.trim="form.phone" type="tel" class="if-input" />
        </div>
        <div>
          <label class="if-label">{{ ct('quote_country') }}</label>
          <input v-model.trim="form.country" type="text" class="if-input" />
        </div>
      </div>

      <!-- Products of interest -->
      <div>
        <label class="if-label mb-1.5">{{ ct('quote_products') }}</label>
        <div class="grid grid-cols-1 gap-1.5">
          <label v-for="p in products" :key="p.id"
            :class="['flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-all',
              form.productInterests.includes(p.id) ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-[#e2e8f0] hover:border-[var(--color-primary-a50)]']">
            <input type="checkbox" :value="p.id" v-model="form.productInterests" class="accent-[var(--color-primary)] w-4 h-4 flex-shrink-0" />
            <span class="text-sm text-[#2d3748]">{{ p.label }}</span>
          </label>
        </div>
      </div>

      <!-- Message -->
      <div>
        <label class="if-label">{{ ct('quote_message') }} <span class="text-red-500">*</span></label>
        <textarea v-model.trim="form.message" rows="4" :placeholder="ct('quote_message_ph')"
          class="if-input resize-y" :class="{ 'if-input-error': errors.message }" @input="errors.message = ''"></textarea>
        <p v-if="errors.message" class="if-error">{{ ct('quote_required') }}</p>
      </div>

      <!-- Submit error -->
      <p v-if="submitError" class="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
        <AlertCircle class="w-4 h-4 flex-shrink-0" />{{ ct(submitError) }}
      </p>

      <button type="submit" :disabled="submitting"
        class="w-full py-2.5 rounded-xl bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-primary-dark)] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
        <Send class="w-4 h-4" :class="isRtl ? '-scale-x-100' : ''" />
        {{ submitting ? ct('quote_submitting') : ct('quote_submit') }}
      </button>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ClipboardList, X, Wrench, Send, AlertCircle } from 'lucide-vue-next'
import { productsData } from '../../data/products'
import { quoteDraft, clearDraft } from '../../services/quoteDraft'
import { postInquiry } from '../../services/chatClient'

const props = defineProps({
  // Widget chat locale — the form renders and submits in this locale, which can
  // differ from the site locale.
  chatLocale: { type: String, default: 'en' }
})
const emit = defineEmits(['submitted', 'close'])

const { t } = useI18n({ useScope: 'global' })
// Translate in the widget's chat locale (may override the site locale).
const ct = (key, named) => t(key, named || {}, { locale: props.chatLocale })

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const isRtl = computed(() => props.chatLocale === 'ar')

const form = reactive({
  name: '', email: '', phone: '', country: '',
  productInterests: [], message: ''
})
const errors = reactive({ name: '', email: '', message: '' })
const submitting = ref(false)
const submitError = ref('')
const includeDraft = ref(true)

const products = computed(() =>
  productsData.map((p) => ({ id: p.id, label: p.name[props.chatLocale] || p.name.en }))
)

// --- attached DIY draft ------------------------------------------------------
const showDraftChip = computed(() => !!quoteDraft.draft && includeDraft.value)
const draftSummary = computed(() => {
  const d = quoteDraft.draft
  if (!d) return ''
  const catP = productsData.find((p) => p.id === d.category)
  const cat = catP ? (catP.name[props.chatLocale] || catP.name.en)
    : (d.category === 'other' ? ct('diy_other') : d.category)
  const parts = [cat, d.fittingType, Array.isArray(d.sizes) ? d.sizes.join(', ') : '', d.quantity]
  return parts.filter(Boolean).join(' · ')
})

// --- submit ------------------------------------------------------------------
function validate() {
  errors.name = form.name ? '' : 'required'
  errors.email = form.email ? (EMAIL_RE.test(form.email) ? '' : 'invalid') : 'required'
  errors.message = form.message ? '' : 'required'
  return !errors.name && !errors.email && !errors.message
}

async function submit() {
  if (submitting.value) return
  submitError.value = ''
  if (!validate()) return
  submitting.value = true
  try {
    const payload = {
      locale: props.chatLocale,
      name: form.name,
      email: form.email,
      phone: form.phone || undefined,
      country: form.country || undefined,
      productInterests: [...form.productInterests],
      message: form.message
    }
    if (quoteDraft.draft && includeDraft.value) payload.diyDraft = quoteDraft.draft
    const data = await postInquiry(payload)
    // The draft has now been filed with the inquiry — clear it so the /quote
    // page and a later inquiry start clean.
    if (quoteDraft.draft && includeDraft.value) clearDraft()
    emit('submitted', { inquiryId: data.inquiryId, autoReply: data.autoReply || '' })
  } catch (e) {
    submitError.value = e && e.offline ? 'chat_offline' : 'chat_error'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.if-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.35rem;
}
.if-input {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  font-size: 0.85rem;
  background: #fff;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.if-input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-a10);
}
.if-input-error { border-color: #ef4444; }
.if-error { margin-top: 0.3rem; font-size: 0.7rem; color: #dc2626; }
</style>
